<template>
    <div class="image-container">
        <img
            :class="props.preview ? 'max-h-10 chat-image w-auto' : ( props.pane ? 'h-40 w-80 object-cover' : ' chat-image max-h-80 w-auto')"
            :src="url"
            alt="Imagem"
            class=" rounded-sm "
            loading="lazy"
            @click="openLightbox"
        />

        <div v-if="progress !== undefined && progress < 100 && progress > 0"
             class="loading-overlay">
            <div class="loading-content">
                <div class="spinner"></div>
                <span class="progress-text">{{ progress }}%</span>
            </div>
        </div>

        <div v-if="caption && !props.pane" class="mt-4 text-sm text-gray-600 dark:text-gray-50" v-html="caption"></div>
    </div>

    <Teleport to="body">
        <div v-if="isOpen" class="lightbox-overlay" @click.self="closeLightbox">
            <div class="top-controls">
                <button class="control-btn" title="Baixar" @click="download">
                    <svg fill="none" height="20" stroke="currentColor" stroke-linecap="round"
                         stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" width="20">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" x2="12" y1="15" y2="3"/>
                    </svg>
                </button>
                <button class="control-btn" title="Fechar" @click="closeLightbox">
                    <svg fill="none" height="24" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                         stroke-width="2" viewBox="0 0 24 24" width="24">
                        <line x1="18" x2="6" y1="6" y2="18"></line>
                        <line x1="6" x2="18" y1="6" y2="18"></line>
                    </svg>
                </button>
            </div>

            <div ref="lightboxContent"
                 class="lightbox-content"
                 @wheel="handleWheel">
                <img
                    ref="lightboxImage"
                    :src="url"
                    :style="{
                        transform: `scale(${zoomLevel})`,
                        transformOrigin: `${originX}px ${originY}px`
                    }"
                    alt="Imagem ampliada"
                    class="lightbox-image"
                    draggable="false"
                />
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from "vue";
import {useChatSessionStore} from "@src/stores/chatSession";
import {useMessageStore} from "@src/stores/messages";
import {getOrFetch} from "@src/composables/useMediaCache";
import {downloadFile} from "@src/helpers/utils";

const props = defineProps(['data', 'preview', 'isGroup', 'pane', 'id'])
const url = ref<string>('')
const caption = ref<string>('')
const isOpen = ref<boolean>(false)
const zoomLevel = ref<number>(1)
const originX = ref<number>(0)
const originY = ref<number>(0)
const isZoomLocked = ref<boolean>(false)
const lightboxContent = ref<HTMLElement | null>(null)
const lightboxImage = ref<HTMLImageElement | null>(null)
const sessionStore = useChatSessionStore()

const progress = computed(() => {
    const messageStore = useMessageStore()
    const message = sessionStore.type === 'session'
        ? sessionStore.selectedSession?.messages?.find(msg => msg.id === props.id)
        : messageStore.groupMessages?.find(msg => msg.id === props.id);

    return message?.progress ?? 0;
})
const openLightbox = () => {
    if (props.preview) {
        return
    }
    if (url.value) {
        isOpen.value = true
        document.body.style.overflow = 'hidden'
    }
}

const closeLightbox = () => {
    isOpen.value = false
    document.body.style.overflow = ''
    resetZoom()
}

const download = () => downloadFile(url.value)

const resetZoom = () => {
    zoomLevel.value = 1
    originX.value = 0
    originY.value = 0
    isZoomLocked.value = false
}

const handleWheel = (e: WheelEvent) => {
    e.preventDefault()

    if (!lightboxImage.value) return

    if (!isZoomLocked.value && zoomLevel.value === 1) {
        const rect = lightboxImage.value.getBoundingClientRect()

        const mouseX = e.clientX - rect.left
        const mouseY = e.clientY - rect.top

        originX.value = mouseX
        originY.value = mouseY

        isZoomLocked.value = true
    }

    const zoomStep = 0.1
    if (e.deltaY < 0) {
        zoomLevel.value = Math.min(zoomLevel.value + zoomStep, 5)
    } else {
        zoomLevel.value = Math.max(zoomLevel.value - zoomStep, 1)
        if (zoomLevel.value === 1) {
            isZoomLocked.value = false
            originX.value = 0
            originY.value = 0
        }
    }
}

onMounted(async () => {
    let rawUrl: string;
    try {
        const parsed = JSON.parse(props.data);
        rawUrl = parsed.url;
        if (parsed.caption !== parsed.url) caption.value = parsed.caption;
    } catch {
        rawUrl = props.data;
    }
    url.value = await getOrFetch(rawUrl);
})
</script>

<style scoped>
.image-container {
    /* Necessário para posicionar o overlay de loading ABSOLUTAMENTE dentro dele */
    position: relative;
    display: inline-block; /* Para que o contêiner não ocupe 100% da largura */
}

.chat-image {
    /* Estilo normal da sua imagem, garantindo que não seja opaca para ver o overlay */
    cursor: pointer;
    display: block;
    max-width: 100%;
    height: auto;
}

.loading-overlay {
    /* Ocupa todo o espaço da imagem */
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /* Fundo semi-transparente para focar no loading */
    background-color: rgba(0, 0, 0, 0.5);
    /* Centralizar o conteúdo (spinner + texto) */
    display: flex;
    justify-content: center;
    align-items: center;
    /* Para que o overlay fique por cima da imagem */
    z-index: 10;
    border-radius: inherit; /* Se a imagem tiver bordas arredondadas */
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: white; /* Cor do texto e spinner */
}

/* --- Estilo de Spinner em 360 (Exemplo básico com CSS) --- */
.spinner {
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top: 4px solid #fff; /* Cor do "progresso" do círculo */
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
    margin-bottom: 5px;
}

.progress-text {
    font-weight: bold;
    font-size: 0.9rem;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

/* Lightbox Styles */
.lightbox-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
}

.top-controls {
    position: fixed;
    top: 20px;
    right: 20px;
    display: flex;
    gap: 8px;
    z-index: 10000;
}

.control-btn {
    border: none;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(4px);
    border-radius: 50%;
    width: 44px;
    height: 44px;
    cursor: pointer;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease, transform 0.2s ease;
}

.control-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
}

.lightbox-content {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
}

.lightbox-image {
    max-width: 90%;
    max-height: 90%;
    transition: transform 0.2s ease;
    user-select: none;
    cursor: crosshair;
}
</style>